
import { supabase } from "@/integrations/supabase/client";

export interface DocumentInfo {
  id: string;
  name: string;
  file_path: string;
}

/**
 * Fetches document information for the current organization
 */
export const fetchOrganizationDocuments = async (): Promise<DocumentInfo[]> => {
  try {
    console.log('Fetching documents to match sources');
    
    // Get the organization ID from user context
    const { data: orgData, error: orgError } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('id', sessionStorage.getItem('organization_member_id'))
      .maybeSingle();
    
    if (orgError) {
      console.error('Error fetching organization ID:', orgError);
      return [];
    }
    
    if (!orgData?.organization_id) {
      console.error('No organization ID found');
      return [];
    }
    
    console.log('Organization ID:', orgData.organization_id);
    
    const { data, error } = await supabase
      .from('documents')
      .select('id, name, file_path')
      .eq('organization_id', orgData.organization_id);
    
    if (error) {
      console.error('Error fetching documents:', error);
      return [];
    }
    
    console.log('Fetched documents:', data?.length || 0);
    if (data) {
      console.log('Document names:', data.map(d => d.name).join(', '));
    }
    
    return data || [];
  } catch (error) {
    console.error('Failed to fetch documents:', error);
    return [];
  }
};

/**
 * Creates a signed URL for document download
 */
export const createDocumentSignedUrl = async (document: DocumentInfo): Promise<string | null> => {
  try {
    console.log('Creating signed URL for document:', document.name, document.file_path);
    
    const { data, error } = await supabase.storage
      .from('documents')
      .createSignedUrl(document.file_path, 60);
    
    if (error) {
      console.error('Error creating signed URL:', error);
      return null;
    }
    
    if (data?.signedUrl) {
      console.log('Created signed URL:', data.signedUrl);
      return data.signedUrl;
    } else {
      console.error('No signed URL returned');
      return null;
    }
  } catch (error) {
    console.error('Error creating signed URL:', error);
    return null;
  }
};

/**
 * Find matching document based on source name with improved matching logic
 */
export const findMatchingDocument = (sourceName: string, documents: DocumentInfo[]): DocumentInfo | undefined => {
  if (!documents?.length) return undefined;
  
  // Remove file extension if present for better matching
  const sourceBaseName = sourceName.toLowerCase().trim();
  
  console.log('Looking for document match for:', sourceBaseName);
  
  // Try various matching strategies
  let match = documents.find(doc => {
    const docName = doc.name.toLowerCase();
    // Check if document name equals source name (with or without extension)
    return docName === sourceBaseName || 
            // Check if source name contains document name
            sourceBaseName.includes(docName) ||
            // Check if source name without extension equals document name without extension
            (sourceBaseName.replace(/\.\w+$/, '') === docName.replace(/\.\w+$/, ''));
  });
  
  // Log matching results
  console.log('Found matching document:', match?.name || 'No match');
  
  return match;
};
